import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ImagePlus, Trash2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import { photoTexts } from './photoTranslations';

// Instagram-style photo composer — up to 5 photos + caption
export const PhotoComposer = ({ onClose, onSuccess }) => {
  const { language } = useLanguage();
  const P = photoTexts(language);
  const [files, setFiles] = useState([]); // {file, preview}
  const [caption, setCaption] = useState('');
  const [publishing, setPublishing] = useState(false);
  const inputRef = useRef(null);
  const keyboardHeight = useKeyboardHeight();

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!incoming.length) return;
    if (files.length + incoming.length > 5) {
      toast.error(P.errTooMany);
      return;
    }
    const next = incoming.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    setFiles((prev) => [...prev, ...next]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[idx]?.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const publish = async () => {
    if (!files.length) {
      toast.error(P.errNone);
      return;
    }
    setPublishing(true);
    try {
      const form = new FormData();
      files.forEach(({ file }) => form.append('images', file));
      form.append('caption', caption);
      await axios.post('/api/photos/create', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(P.published);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      console.error('Photo publish error:', e);
      toast.error(P.errUpload);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
      style={{
        paddingBottom: keyboardHeight > 0 ? `${keyboardHeight + 16}px` : undefined,
        alignItems: keyboardHeight > 0 ? 'flex-start' : 'center',
        paddingTop: keyboardHeight > 0 ? '20px' : undefined,
      }}
      onClick={onClose}
      data-testid="photo-composer-modal"
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full p-5 shadow-2xl overflow-y-auto"
        style={{ maxHeight: keyboardHeight > 0 ? `calc(100dvh - ${keyboardHeight}px - 40px)` : '90vh', transition: 'max-height 180ms ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{P.composerTitle}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="photo-composer-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Previews grid */}
        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {files.map((f, idx) => (
              <div key={idx} className="relative aspect-square rounded-xl overflow-hidden bg-black/40">
                <img src={f.preview} alt={`photo-${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeFile(idx)}
                  className="absolute top-1 right-1 p-1.5 bg-black/70 rounded-full hover:bg-red-600 transition-colors"
                  data-testid={`photo-composer-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length < 5 && (
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full mb-3 py-6 border-2 border-dashed border-white/20 rounded-2xl hover:border-fuchsia-500/60 hover:bg-white/5 transition-all flex flex-col items-center gap-2 text-gray-300"
            data-testid="photo-composer-add-btn"
          >
            <ImagePlus className="w-8 h-8 text-fuchsia-400" />
            <span className="font-semibold">{P.addPhotos}</span>
            <span className="text-xs text-gray-500">{P.maxHint} ({files.length}/5)</span>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          data-testid="photo-composer-input"
        />

        <textarea
          value={caption}
          onChange={(e) => {
            const v = e.target.value;
            if (v.split('\n').length > 10) {
              toast.error(P.maxLines);
              setCaption(v.split('\n').slice(0, 10).join('\n'));
              return;
            }
            setCaption(v);
          }}
          placeholder={P.captionPlaceholder}
          maxLength={2200}
          rows={3}
          onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250)}
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-fuchsia-500/60 mb-4"
          data-testid="photo-composer-caption"
        />

        <button
          onClick={publish}
          disabled={publishing || !files.length}
          className="w-full py-3 bg-gradient-to-r from-pink-500 via-fuchsia-600 to-purple-700 rounded-full font-bold text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
          data-testid="photo-composer-publish"
        >
          {publishing ? P.publishing : P.publish}
        </button>
      </div>
    </div>
  );
};

export default PhotoComposer;
